import React from 'react';
import { motion } from 'framer-motion';
import { Room, PatientStatus, STATUS_CONFIG } from '../types';
import { Activity, CheckCircle, Clock, ArrowRight, Plus } from 'lucide-react';

interface RoomCardProps {
  room: Room;
  patientStatus?: PatientStatus;
  patientCode?: string;
  onClick: () => void;
  primaryColor?: string;
  isDarkMode?: boolean;
}

const RoomCard: React.FC<RoomCardProps> = ({ room, patientStatus, patientCode, onClick, primaryColor, isDarkMode = false }) => {
  // A room is occupied if it has a currentPatientId OR if StaffDashboard found a patient mapped to this room (Ghost Patient prevention)
  const isOccupied = !!room.currentPatientId || !!patientCode;

  // Determine card style based on status
  let statusColor = "bg-slate-100 text-slate-500";

  const config = patientStatus ? STATUS_CONFIG[patientStatus] : null;

  if (isOccupied && config) {
    statusColor = `${config.color || 'bg-slate-200'} ${config.text || 'text-slate-500'}`;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative p-5 sm:p-6 rounded-3xl transition-all duration-300 border cursor-pointer h-auto min-h-[10rem] sm:h-48 flex flex-col justify-between group overflow-hidden ${isDarkMode
        ? `bg-[#181b21] shadow-none ${isOccupied ? 'border-indigo-500/20 shadow-indigo-500/10' : 'border-white/5 hover:border-white/10'}`
        : `bg-white shadow-sm hover:shadow-md ${isOccupied ? 'border-med-accent/10 shadow-med-accent/5' : 'border-slate-200/60 shadow-slate-200/20'}`
        }`}
    >
      {/* Background Decorative Element */}
      <div
        className={`absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 rounded-full opacity-[0.03] transition-transform duration-700 group-hover:scale-150 ${isOccupied && patientStatus && patientStatus !== PatientStatus.SURGERY ? (config?.color || 'bg-slate-400') : 'bg-slate-400'}`}
        style={isOccupied && patientStatus === PatientStatus.SURGERY ? { backgroundColor: primaryColor || '#5B65DC' } : {}}
      />

      {/* Status Bar Indicator */}
      <div className={`absolute top-0 left-0 bottom-0 w-1.5 ${isOccupied && config ? (isDarkMode ? (config.color?.replace('bg-', 'bg-').replace('50', '500') || 'bg-slate-500') : config.color) : (isDarkMode ? 'bg-[#252830]' : 'bg-slate-200')} transition-colors duration-500`} />

      <div className="flex justify-between items-start pl-2">
        <div className="flex flex-col">
          <h3 className={`text-xl font-extrabold transition-colors tracking-tight leading-tight ${isDarkMode ? 'text-white group-hover:text-indigo-400' : 'text-med-navy group-hover:text-med-accent'}`}>{room.name}</h3>
          {!isOccupied && <span className={`text-[10px] font-bold uppercase tracking-widest mt-1 pl-0.5 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`}>Área Disponible</span>}
        </div>

        {isOccupied && config ? (
          <div
            className={`p-3 rounded-2xl bg-opacity-10 shadow-inner ${isDarkMode ? `${(config.text || '').replace('text-', 'text-').replace('600', '400')} ${(config.color || '').replace('bg-', 'bg-').replace('50', '500/10')}` : `${config.text} ${patientStatus !== PatientStatus.SURGERY ? config.color : ''}`}`}
            style={patientStatus === PatientStatus.SURGERY ? { backgroundColor: primaryColor ? `${primaryColor}1a` : '#5B65DC1a', color: primaryColor || '#5B65DC' } : {}}
          >
            <Activity className="w-6 h-6 animate-pulse" />
          </div>
        ) : isOccupied && !config ? (
          <div className={`p-3 rounded-2xl ${isDarkMode ? 'bg-[#252830] text-white/50' : 'bg-slate-100 text-slate-400'}`}>
            <Activity className="w-6 h-6" />
          </div>
        ) : (
          <div className={`p-3 rounded-2xl border ${isDarkMode ? 'bg-[#1e2128] text-white/20 border-white/5' : 'bg-slate-50 text-slate-200 border-slate-100'}`}>
            <CheckCircle className="w-6 h-6" />
          </div>
        )}
      </div>

      <div className="mt-auto pl-2">
        {isOccupied ? (
          <div className="flex items-end justify-between">
            <div>
              <div className={`flex items-center space-x-2 mb-3 ${isDarkMode ? 'text-white' : 'text-med-navy'}`}>
                <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: primaryColor || '#5B65DC' }} />
                <span className={`font-mono text-xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-med-navy'}`}>
                  {patientCode || <span className="text-sm text-slate-400 opacity-60">Ingresando...</span>}
                </span>
              </div>
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider shadow-sm shadow-current/10 border backdrop-blur-sm ${patientStatus !== PatientStatus.SURGERY ? (isDarkMode ? `${statusColor.replace('bg-', 'bg-').replace('50', '500/10').replace('text-', 'text-').replace('600', '400')} border-transparent` : `${statusColor} border-white/40`) : (isDarkMode ? 'text-white border-transparent' : 'text-white border-white/40')
                  }`}
                style={patientStatus === PatientStatus.SURGERY ? { backgroundColor: primaryColor || '#5B65DC' } : {}}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                {config ? config.label : 'SINCRONIZANDO...'}
              </div>
            </div>

            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all transform group-hover:scale-105 group-hover:rotate-[-5deg] shadow-sm border ${isDarkMode ? 'bg-[#252830] border-white/5' : 'bg-slate-50 border-slate-100/50'}`}
              style={{ color: primaryColor || '#5B65DC' }}
            >
              <ArrowRight className="w-5 h-5" />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between mt-6">
            <div className={`flex items-center font-bold text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-lg border ${isDarkMode ? 'bg-[#252830] border-white/5 text-white/50' : 'bg-slate-50 border-slate-100 text-slate-500'}`}>
              <Clock className={`w-3.5 h-3.5 mr-1.5 ${isDarkMode ? 'text-white/30' : 'text-slate-400'}`} />
              <span>Libre</span>
            </div>

            <button
              className="flex items-center gap-1.5 text-[10px] font-bold text-white px-5 py-2.5 rounded-[14px] transition-all shadow-sm active:scale-95 uppercase tracking-wider hover:opacity-90"
              style={{ backgroundColor: primaryColor || '#122056' }}
            >
              <Plus className="w-3.5 h-3.5" />
              Ingresar
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default RoomCard;